import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Logo, BRAND_NAME, BRAND_SUBTITLE } from "../components/ui/Logo";
import { Check, Megaphone } from "lucide-react";
import { submitPublicAdvertisingForm } from "../lib/api";

const APP_LOGIN_URL =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "/login"
    : process.env.REACT_APP_APP_URL || "https://app.aurorageriatrica.com/login";

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "contacto@aurorageriatrica.com";

const PLAN_CARDS = [
  {
    name: "Plan Básico",
    price: "$70",
    period: "/ 30 días",
    highlight: false,
    items: ["Login", "Landing secundaria", "Ideal para cursos, congresos y diplomados"],
  },
  {
    name: "Plan Profesional",
    price: "$150",
    period: "/ 30 días",
    highlight: true,
    items: ["Dashboard superior", "Landing principal", "Ideal para marcas con enfoque médico"],
  },
  {
    name: "Plan Premium",
    price: "$320",
    period: "/ 30 días",
    highlight: false,
    items: [
      "Dashboard superior",
      "Dashboard lateral",
      "Landing principal",
      "Landing intermedia",
      "Mayor visibilidad durante 30 días",
    ],
  },
];

const STEPS = [
  "Eliges tu plan",
  "Envías imagen o video + enlace",
  "Activamos tu campaña",
  "Tu marca aparece durante 30 días",
];

const initialAdForm = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  ad_type: "banner",
  budget_usd: 0,
  message: "",
};

export default function Marketing() {
  const [adForm, setAdForm] = useState(initialAdForm);
  const [adLoading, setAdLoading] = useState(false);
  const [adSuccess, setAdSuccess] = useState(false);
  const [adError, setAdError] = useState("");

  const handleAdChange = (e) => {
    const { name, value } = e.target;
    if (name === "budget_usd") {
      setAdForm((prev) => ({ ...prev, budget_usd: value === "" ? 0 : Number(value) }));
      return;
    }
    setAdForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdSubmit = async (e) => {
    e.preventDefault();
    setAdError("");
    if (!adForm.company_name.trim()) {
      setAdError("La empresa u organización es obligatoria.");
      return;
    }
    if (!adForm.contact_name.trim()) {
      setAdError("El nombre del contacto es obligatorio.");
      return;
    }
    if (!adForm.email.trim()) {
      setAdError("El email es obligatorio.");
      return;
    }
    if (!adForm.ad_type) {
      setAdError("Selecciona un tipo de anuncio.");
      return;
    }
    setAdLoading(true);
    try {
      const budgetNum = Number(adForm.budget_usd);
      await submitPublicAdvertisingForm({
        ...adForm,
        budget_usd: Number.isNaN(budgetNum) ? 0 : budgetNum,
      });
      setAdSuccess(true);
      setAdForm(initialAdForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAdError(typeof detail === "string" ? detail : "Error al enviar. Intenta de nuevo.");
    } finally {
      setAdLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-800 font-semibold">
            <Logo variant="icon" size="md" />
            {BRAND_NAME}
          </Link>
          <Link to={APP_LOGIN_URL}>
            <Button variant="outline" size="sm" className="border-slate-300">
              Iniciar sesión
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-sky-100 text-sky-600 mb-6">
            <Megaphone className="h-7 w-7" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Marketing en Aurora Geriátrica
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Conecta tu marca con médicos que atienden adultos mayores en su práctica diaria.
          </p>
        </div>

        {/* Qué ofrecemos */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Qué ofrecemos</h2>
          <p className="text-slate-600 leading-relaxed">
            Aurora Geriátrica ofrece espacios publicitarios dentro de su ecosistema digital para
            marcas, congresos, universidades, clínicas y servicios vinculados a la salud y la atención
            del adulto mayor. Así tu mensaje llega a profesionales que usan la plataforma cada día.
          </p>
        </section>

        {/* Espacios disponibles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Espacios disponibles</h2>
          <ul className="space-y-3 text-slate-700">
            <li className="flex items-start gap-2">
              <Check className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <span>Landing principal</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <span>Pantalla de acceso</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <span>Panel principal de la plataforma</span>
            </li>
          </ul>
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
            <strong className="font-semibold">Importante:</strong> no mostramos publicidad en
            informes PDF ni en documentos entregados a pacientes o familias.
          </div>
        </section>

        {/* Planes */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-10">Planes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PLAN_CARDS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl border bg-white p-6 shadow-sm flex flex-col ${
                  plan.highlight ? "border-2 border-sky-400 ring-2 ring-sky-100" : "border-slate-200"
                }`}
              >
                <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                <p className="mt-3 text-2xl font-bold text-slate-900">
                  {plan.price}{" "}
                  <span className="text-base font-normal text-slate-500">{plan.period}</span>
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                  {plan.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <Check className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Cómo funciona</h2>
          <ol className="space-y-4 text-slate-700">
            {STEPS.map((step, i) => (
              <li key={step} className="flex gap-4">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-sky-700">
                  {i + 1}
                </span>
                <span className="pt-1">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Formatos */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Formatos aceptados</h2>
          <ul className="space-y-2 text-slate-600">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-sky-600" />
              Imagen
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-sky-600" />
              Video corto
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-sky-600" />
              Enlace externo del anunciante
            </li>
          </ul>
        </section>

        {/* Público objetivo */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Público objetivo</h2>
          <p className="text-slate-600 leading-relaxed">
            Médicos y profesionales de la salud que valoran herramientas específicas para la
            geriatría: desde la consulta ambulatoria hasta el seguimiento longitudinal, con foco en
            el adulto mayor y su entorno asistencial.
          </p>
        </section>

        {/* Solicitud publicidad */}
        <section className="rounded-2xl border border-slate-200 bg-white py-10 px-6 sm:px-10 shadow-sm">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
            Solicita información de publicidad
          </h2>
          <p className="mt-2 text-center text-slate-600 text-sm max-w-xl mx-auto">
            Completa el formulario y te contactamos en menos de 48 horas
          </p>

          {adSuccess ? (
            <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center">
              <p className="text-lg font-semibold text-emerald-900">✓ Solicitud recibida</p>
              <p className="mt-2 text-sm text-emerald-800">
                Te contactaremos en 24–48 horas al correo que indicaste.
              </p>
            </div>
          ) : (
            <form onSubmit={handleAdSubmit} className="mt-8 max-w-lg mx-auto space-y-4 text-left">
              <div>
                <label htmlFor="ad_company_name" className="block text-xs font-medium text-slate-700 mb-1">
                  Empresa u organización <span className="text-red-600">*</span>
                </label>
                <input
                  id="ad_company_name"
                  name="company_name"
                  type="text"
                  required
                  value={adForm.company_name}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="ad_contact_name" className="block text-xs font-medium text-slate-700 mb-1">
                  Nombre del contacto <span className="text-red-600">*</span>
                </label>
                <input
                  id="ad_contact_name"
                  name="contact_name"
                  type="text"
                  required
                  value={adForm.contact_name}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="ad_email" className="block text-xs font-medium text-slate-700 mb-1">
                  Email de contacto <span className="text-red-600">*</span>
                </label>
                <input
                  id="ad_email"
                  name="email"
                  type="email"
                  required
                  value={adForm.email}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="ad_phone" className="block text-xs font-medium text-slate-700 mb-1">
                  Teléfono / WhatsApp
                </label>
                <input
                  id="ad_phone"
                  name="phone"
                  type="text"
                  value={adForm.phone}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="ad_type" className="block text-xs font-medium text-slate-700 mb-1">
                  Tipo de anuncio <span className="text-red-600">*</span>
                </label>
                <select
                  id="ad_type"
                  name="ad_type"
                  required
                  value={adForm.ad_type}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="banner">Banner en la plataforma</option>
                  <option value="sponsored">Contenido patrocinado</option>
                  <option value="email">Email a médicos</option>
                  <option value="recursos">Artículo en Recursos</option>
                </select>
              </div>
              <div>
                <label htmlFor="ad_budget" className="block text-xs font-medium text-slate-700 mb-1">
                  Presupuesto estimado
                </label>
                <select
                  id="ad_budget"
                  name="budget_usd"
                  value={adForm.budget_usd === 0 ? "0" : String(adForm.budget_usd)}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="0">No definido aún</option>
                  <option value="100">USD $100 - $300/mes</option>
                  <option value="300">USD $300 - $500/mes</option>
                  <option value="500">USD $500 - $1000/mes</option>
                  <option value="1000">USD $1000+/mes</option>
                </select>
              </div>
              <div>
                <label htmlFor="ad_message" className="block text-xs font-medium text-slate-700 mb-1">
                  Mensaje adicional
                </label>
                <textarea
                  id="ad_message"
                  name="message"
                  rows={3}
                  value={adForm.message}
                  onChange={handleAdChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y min-h-[4.5rem]"
                />
              </div>
              {adError && (
                <p className="text-sm text-red-600" role="alert">
                  {adError}
                </p>
              )}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={adLoading}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-sky-700 text-white text-sm font-semibold hover:bg-sky-800 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {adLoading ? "Enviando..." : "Enviar solicitud"}
                </button>
              </div>
            </form>
          )}
          <p className="mt-6 text-center text-slate-500 text-sm">
            También puedes escribirnos a{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sky-700 font-medium hover:underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </section>

        {/* Footer links */}
        <footer className="mt-16 py-8 border-t border-slate-200">
          <div className="flex flex-wrap justify-center gap-6 text-slate-500 text-sm mb-4">
            <Link to="/precios" className="hover:text-sky-600">
              Precios
            </Link>
            <Link to="/marketing" className="hover:text-sky-600">
              Marketing
            </Link>
            <Link to="/contacto" className="hover:text-sky-600">
              Contacto
            </Link>
            <Link to="/privacidad" className="hover:text-sky-600">
              Privacidad
            </Link>
            <Link to="/terminos" className="hover:text-sky-600">
              Términos
            </Link>
            <Link to={APP_LOGIN_URL} className="hover:text-sky-600">
              Iniciar sesión
            </Link>
          </div>
          <p className="text-center text-slate-500 text-sm">
            {BRAND_NAME} · {BRAND_SUBTITLE}
          </p>
        </footer>
      </main>
    </div>
  );
}
